/**
 * Letter-tile renderer — the centerpiece of the Anagram round scene.
 *
 * Layout rules (mirrors Who Am I):
 *   - 2-word phrases: ALWAYS one word per line.
 *   - 3+ word phrases: pack words on a line until letter count > 9, then wrap.
 *   - Single word > 9 letters: keep it on one line; scale ALL tiles down
 *     uniformly so the longest line fits within the container budget (1180px).
 *
 * Visual state per tile:
 *   - default          gold letter on dark blue card
 *   - hint-snap        celebration green; in its CORRECT (unscrambled) slot
 *   - cascade-landing  green flash + flip-in as the cascade reveals it
 *
 * Animations:
 *   - `snapHintLetter(letter)` — at 50% timer reveal: turn the FIRST occurrence
 *     of `letter` green, FLIP-slide it to the position it occupies in the
 *     unscrambled answer, and shift the other tiles to fill the vacated slot.
 *   - `cascadeReveal()` — at round end: each remaining unsnapped letter
 *     reorders to its correct answer-slot one by one, ~200ms stagger,
 *     ~1.4s total for a 7-letter word, with a brief flip + green flash on
 *     each landing tile.
 *
 * @packageDocumentation
 */

export interface LetterTilesHandle {
  el: HTMLDivElement;
  /** Render a fresh jumbled word + remember the answer for snap math. */
  setWord(jumbled: string, answer: string): void;
  /** 50% hint reveal: snap one letter into its correct slot. */
  snapHintLetter(letter: string): void;
  /** End-of-round cascade: reorder remaining tiles into the answer one by one. */
  cascadeReveal(): Promise<void>;
  dispose(): void;
}

const STYLES = `
  @keyframes an-tile-in {
    0%   { opacity: 0; transform: translateY(-30px) scale(0.7) rotate(-6deg); }
    60%  { opacity: 1; transform: translateY(4px) scale(1.04) rotate(2deg); }
    100% { opacity: 1; transform: translateY(0) scale(1) rotate(0); }
  }
  @keyframes an-tile-bob {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-6px); }
  }
  @keyframes an-tile-flash {
    0%   { transform: scale(1); }
    40%  { transform: scale(1.18) rotate(-3deg); box-shadow: 0 0 80px rgba(34,197,94,0.85); }
    100% { transform: scale(1) rotate(0); }
  }
  .an-tiles-wrap {
    /* Vertical stack of rows; each .an-row is a horizontal flex of tiles. */
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    /* Cap so we never overlap the right-side leaderboard. */
    max-width: 1180px;
    width: 100%;
    margin: 0 auto;
    padding: 12px 0;
    pointer-events: none;
  }
  .an-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    gap: 14px;
  }
  .an-tile {
    position: relative;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--an-tile-size, 110px);
    height: var(--an-tile-size, 110px);
    border-radius: 14px;
    background: linear-gradient(180deg, #0E1A6B 0%, #060CE9 60%, #050A8E 100%);
    border: 4px solid var(--sg-primary, #FFC43D);
    box-shadow:
      0 10px 0 #03073D,
      0 16px 26px rgba(0,0,0,0.55),
      inset 0 6px 14px rgba(255,255,255,0.10);
    color: var(--sg-primary, #FFC43D);
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-weight: 700;
    font-size: var(--an-tile-font, 78px);
    line-height: 1;
    letter-spacing: 0;
    text-shadow:
      0 4px 0 #03073D,
      0 0 28px rgba(255,196,61,0.55);
    animation: an-tile-in 480ms cubic-bezier(.18,.85,.4,1.0) both, an-tile-bob 4.6s ease-in-out infinite;
    animation-delay: var(--an-tile-delay, 0ms), 700ms;
    /* CSS transition only kicks in for the FLIP move; default = no transition. */
    transition: transform 720ms cubic-bezier(.22,.95,.4,1.05), opacity 480ms ease-out;
    will-change: transform;
  }
  .an-tile.is-snapped {
    border-color: var(--sg-celebration, #22C55E);
    color: #FFFFFF;
    background: linear-gradient(180deg, #1B3F26 0%, #1F8A3D 55%, #155A28 100%);
    text-shadow: 0 4px 0 #003913, 0 0 36px rgba(34,197,94,0.85);
    box-shadow:
      0 10px 0 #062E10,
      0 0 60px rgba(34,197,94,0.55),
      0 16px 26px rgba(0,0,0,0.55),
      inset 0 6px 14px rgba(255,255,255,0.18);
    animation: an-tile-bob 3.4s ease-in-out infinite;
    animation-delay: 0ms;
  }
  .an-tile.is-flashing {
    animation: an-tile-flash 480ms cubic-bezier(.18,.85,.4,1.0) both;
  }
  .an-word-gap {
    flex: 0 0 28px;
    height: 1px;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('an-tiles-style')) return;
  const s = document.createElement('style');
  s.id = 'an-tiles-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

// ---------------------------------------------------------------------------
// Layout helper (pure, exported for tests)
// ---------------------------------------------------------------------------

/** Container budget — keep tiles clear of the right-side LB at left=1500..1880. */
export const STAGE_LETTER_BUDGET_PX = 1180;
const BASE_TILE = { size: 110, font: 78 } as const;
const TILE_GAP = 14;
const WORD_GAP_EXTRA = 28;

export interface LayoutWord {
  letters: string[];
}

export interface LayoutRow {
  /** Tokens that make up this visual row — each token is one word. */
  tokens: LayoutWord[];
}

export interface LayoutResult {
  rows: LayoutRow[];
  tileSize: { size: number; font: number };
}

/**
 * Compute the row layout for a phrase given its visible letters.
 *
 * Rules:
 *   - 2-word phrases: ALWAYS one word per line (regardless of length).
 *   - 3+ word phrases: pack words onto one line until total LETTER count
 *     in that line would exceed 9, then start a new line.
 *   - Single word > 9 letters: keep on one line; scale ALL tiles down
 *     uniformly so the longest line fits within the container width.
 */
export function layoutLetterRows(phrase: string): LayoutResult {
  const upper = phrase.toUpperCase();
  const words: LayoutWord[] = upper
    .split(/\s+/)
    .filter((w) => w.length > 0)
    .map((w) => ({ letters: [...w] }));

  if (words.length === 0) {
    return { rows: [], tileSize: { ...BASE_TILE } };
  }

  let rows: LayoutRow[];
  if (words.length === 2) {
    rows = words.map((w) => ({ tokens: [w] }));
  } else if (words.length === 1) {
    rows = [{ tokens: [words[0]!] }];
  } else {
    // 3+ words: pack until letter count > 9.
    rows = [];
    let line: LayoutWord[] = [];
    let lineLetters = 0;
    for (const w of words) {
      const wlen = w.letters.length;
      if (line.length > 0 && lineLetters + wlen > 9) {
        rows.push({ tokens: line });
        line = [];
        lineLetters = 0;
      }
      line.push(w);
      lineLetters += wlen;
    }
    if (line.length > 0) rows.push({ tokens: line });
  }

  // Compute the worst-case row dimensions (most tiles + most word gaps).
  let maxTiles = 0;
  let maxRowW = 0;
  let maxRowGapsPx = 0;
  for (const row of rows) {
    const totalTiles = row.tokens.reduce((s, w) => s + w.letters.length, 0);
    const wordGaps = Math.max(0, row.tokens.length - 1);
    const tileGapPx = Math.max(0, totalTiles - 1) * TILE_GAP;
    const wordGapPx = wordGaps * WORD_GAP_EXTRA;
    const w = totalTiles * BASE_TILE.size + tileGapPx + wordGapPx;
    if (totalTiles > maxTiles) maxTiles = totalTiles;
    if (w > maxRowW) maxRowW = w;
    if (tileGapPx + wordGapPx > maxRowGapsPx) maxRowGapsPx = tileGapPx + wordGapPx;
  }

  let scale = 1;
  if (maxRowW > STAGE_LETTER_BUDGET_PX && maxTiles > 0) {
    // Solve for tile size that makes (tiles*size + gaps) <= budget.
    const tileBudget = Math.max(0, STAGE_LETTER_BUDGET_PX - maxRowGapsPx);
    scale = tileBudget / (maxTiles * BASE_TILE.size);
  }
  // Floor at a readable minimum (~36px font) so absurd 20+ letter words still
  // produce visible tiles.
  const minScale = 36 / BASE_TILE.font;
  if (scale < minScale) scale = minScale;

  // Floor (not round) tile size when scaling down so the actual rendered
  // width never exceeds the container budget.
  return {
    rows,
    tileSize: {
      size: scale < 1 ? Math.floor(BASE_TILE.size * scale) : Math.round(BASE_TILE.size * scale),
      font: scale < 1 ? Math.floor(BASE_TILE.font * scale) : Math.round(BASE_TILE.font * scale),
    },
  };
}

// ---------------------------------------------------------------------------
// Tile state + slot model
// ---------------------------------------------------------------------------

interface TileState {
  /** The character on this tile. */
  ch: string;
  el: HTMLDivElement;
  /** True once this tile has been snapped into its correct answer slot. */
  snapped: boolean;
}

/**
 * FLIP shared helper: given pre-mutation rects (`firsts`) and tiles already in
 * their final DOM positions, animate each tile from its old rect to (0,0) over
 * `durationMs`. Used by both snapHintLetter and cascadeReveal.
 */
function flipTilesFrom(tilesArr: TileState[], firsts: DOMRect[], durationMs: number): void {
  requestAnimationFrame(() => {
    for (let i = 0; i < tilesArr.length; i++) {
      const tile = tilesArr[i]!;
      const last = tile.el.getBoundingClientRect();
      const first = firsts[i] ?? last;
      const dx = first.left - last.left;
      const dy = first.top - last.top;
      if (dx === 0 && dy === 0) continue;
      tile.el.style.transition = 'none';
      tile.el.style.transform = `translate(${dx}px, ${dy}px)`;
      void tile.el.getBoundingClientRect();
      requestAnimationFrame(() => {
        tile.el.style.transition = `transform ${durationMs}ms cubic-bezier(.22,.95,.4,1.05)`;
        tile.el.style.transform = '';
      });
    }
  });
}

interface AnswerSlot {
  /** The character that belongs in this slot (uppercase). */
  ch: string;
  /** Which "word" position in the answer (for inter-word gap rendering). */
  wordIdx: number;
  /** Which "letter within word" position. */
  letterIdx: number;
}

/**
 * Build the canonical answer slots: each visible (non-space) character of the
 * answer becomes one slot in display order. Spaces are NOT slots — the layout
 * inserts a `.an-word-gap` div between words instead.
 */
function buildAnswerSlots(answer: string): AnswerSlot[] {
  const slots: AnswerSlot[] = [];
  const upper = answer.toUpperCase();
  const words = upper.split(/\s+/).filter((w) => w.length > 0);
  for (let wi = 0; wi < words.length; wi++) {
    for (let li = 0; li < words[wi]!.length; li++) {
      slots.push({ ch: words[wi]![li]!, wordIdx: wi, letterIdx: li });
    }
  }
  return slots;
}

/**
 * Map a list of available characters (the jumbled letters) onto a list of
 * answer slots. Each slot is filled with a tile carrying that slot's
 * character. When `snappedSlots` are present, those slots will be filled with
 * the matching jumbled-tile in advance (and tagged snapped); the rest are
 * filled with remaining tiles in any order so the user still sees a jumble.
 *
 * Returns the ordered list of (tile-character, isSnapped) per slot.
 */
function arrangeJumble(
  jumbled: string,
  answer: string,
  snappedIndices: number[],
): Array<{ ch: string; snapped: boolean }> {
  const slots = buildAnswerSlots(answer);
  const result: Array<{ ch: string; snapped: boolean } | null> = slots.map(() => null);
  const jumbleChars = [...jumbled.toUpperCase()].filter((c) => c !== ' ');

  // Snapped slots take their canonical answer letter from the bag.
  for (const si of snappedIndices) {
    const slot = slots[si];
    if (!slot) continue;
    const idx = jumbleChars.indexOf(slot.ch);
    if (idx >= 0) jumbleChars.splice(idx, 1);
    result[si] = { ch: slot.ch, snapped: true };
  }
  // Remaining slots get the jumbled letters in their existing order.
  for (let i = 0; i < result.length; i++) {
    if (result[i] === null) {
      const ch = jumbleChars.shift() ?? '?';
      result[i] = { ch, snapped: false };
    }
  }
  return result.map((r) => r!);
}

// ---------------------------------------------------------------------------
// Public factory
// ---------------------------------------------------------------------------

export function createLetterTiles(mount: HTMLElement): LetterTilesHandle {
  ensureStyles();
  const wrap = document.createElement('div');
  wrap.className = 'an-tiles-wrap';
  wrap.setAttribute('data-role', 'letter-tiles');
  mount.appendChild(wrap);

  let tiles: TileState[] = [];
  let answerSlots: AnswerSlot[] = [];
  let lastJumbled = '';
  let lastAnswer = '';
  let snappedSet = new Set<number>(); // slot indices already snapped

  /**
   * Render the current `tiles` array into the DOM, organised into rows
   * matching the `answer` layout. Each tile occupies the slot at the same
   * index in `answerSlots`.
   */
  function render(): void {
    const { rows, tileSize } = layoutLetterRows(lastAnswer);
    wrap.style.setProperty('--an-tile-size', `${tileSize.size}px`);
    wrap.style.setProperty('--an-tile-font', `${tileSize.font}px`);
    wrap.innerHTML = '';

    let slotIdx = 0;
    for (const row of rows) {
      const rowEl = document.createElement('div');
      rowEl.className = 'an-row';
      for (let wi = 0; wi < row.tokens.length; wi++) {
        const word = row.tokens[wi]!;
        for (let li = 0; li < word.letters.length; li++) {
          const tile = tiles[slotIdx];
          if (tile) {
            tile.el.style.setProperty('--an-tile-delay', `${slotIdx * 60}ms`);
            tile.el.classList.toggle('is-snapped', tile.snapped);
            rowEl.appendChild(tile.el);
          }
          slotIdx += 1;
        }
        if (wi < row.tokens.length - 1) {
          const gap = document.createElement('div');
          gap.className = 'an-word-gap';
          rowEl.appendChild(gap);
        }
      }
      wrap.appendChild(rowEl);
    }
  }

  /**
   * Build new tile DOM nodes for the current jumble + answer. Tiles are
   * indexed by answer-slot order; jumbled letters are scattered across slots.
   */
  function buildTiles(): void {
    tiles = [];
    snappedSet.clear();
    answerSlots = buildAnswerSlots(lastAnswer);
    const arrangement = arrangeJumble(lastJumbled, lastAnswer, []);
    for (let i = 0; i < arrangement.length; i++) {
      const a = arrangement[i]!;
      const el = document.createElement('div');
      el.className = 'an-tile';
      el.textContent = a.ch;
      el.setAttribute('data-slot', String(i));
      tiles.push({ ch: a.ch, el, snapped: a.snapped });
    }
  }

  function setWord(jumbled: string, answer: string): void {
    if (jumbled === lastJumbled && answer === lastAnswer) return;
    lastJumbled = jumbled;
    lastAnswer = answer;
    buildTiles();
    render();
  }

  /**
   * 50% hint reveal: take the FIRST tile carrying `letter` (in slot order),
   * find the FIRST unsnapped slot whose answer-character matches `letter`,
   * swap them, mark snapped, and FLIP-animate the move + green flash.
   */
  function snapHintLetter(letter: string): void {
    const upper = letter.toUpperCase();
    if (!upper) return;
    if (answerSlots.length === 0) return;
    // Find target slot: first unsnapped slot whose canonical char === letter.
    let targetSlot = -1;
    for (let i = 0; i < answerSlots.length; i++) {
      if (snappedSet.has(i)) continue;
      if (answerSlots[i]!.ch === upper) {
        targetSlot = i;
        break;
      }
    }
    if (targetSlot < 0) return; // no matching answer slot left
    // Find source tile: first tile carrying the letter that ISN'T already
    // sitting in its correct slot (snapped). Prefer a tile NOT in targetSlot.
    let sourceIdx = -1;
    for (let i = 0; i < tiles.length; i++) {
      if (tiles[i]!.ch === upper && !tiles[i]!.snapped) {
        sourceIdx = i;
        break;
      }
    }
    if (sourceIdx < 0) return;

    // Capture FLIP "first" rect for ALL tiles before mutating order.
    const firsts = tiles.map((t) => t.el.getBoundingClientRect());

    if (sourceIdx !== targetSlot) {
      // Swap source -> target. The tile currently in targetSlot bumps to
      // sourceIdx (a "fill the vacated slot" shift).
      const tmp = tiles[targetSlot]!;
      tiles[targetSlot] = tiles[sourceIdx]!;
      tiles[sourceIdx] = tmp;
    }
    tiles[targetSlot]!.snapped = true;
    snappedSet.add(targetSlot);

    render();
    flipTilesFrom(tiles, firsts, 520);
    // Flash the snapped tile.
    const snappedTile = tiles[targetSlot]!;
    requestAnimationFrame(() => {
      snappedTile.el.classList.add('is-flashing');
      setTimeout(() => snappedTile.el.classList.remove('is-flashing'), 520);
    });
  }

  /**
   * Reveal-cascade animation: walk the answer slots in order; for every slot
   * not yet snapped, find a remaining tile carrying that character, move it
   * into place with a brief flip + green flash. ~200ms stagger between
   * letters; ~420ms per landing animation, fully interleaved.
   *
   * Bug-fix history (vs the original implementation):
   *   - sourceIdx was captured at scheduling time, but the inner search
   *     started at `slot` (so a matching tile at index < slot could be
   *     missed) AND the actual swap fired later inside setTimeout — by then
   *     a previous step may have moved the tile, leaving us with a stale
   *     index pointing at the wrong character. Words with repeated letters
   *     (BANANA, BUTTERFLY) routinely landed in the wrong order.
   *   - Layout was captured BEFORE the previous step's FLIP transform had
   *     settled, so per-step rects were occasionally stale.
   *
   * Fix: Both the source-index search and the FLIP "first" rect capture
   * happen INSIDE the per-step setTimeout — every step re-measures from the
   * live DOM and re-scans the live `tiles` array. We also explicitly track
   * which tile-index goes to which target-slot per step (no character-only
   * dedupe).
   *
   * Resolves once the last cascade animation has completed.
   */
  function cascadeReveal(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (answerSlots.length === 0) {
        resolve();
        return;
      }
      const stepMs = 200;
      const flipMs = 420;
      let stepIdx = 0;
      let lastLandMs = 0;

      for (let slot = 0; slot < answerSlots.length; slot++) {
        if (snappedSet.has(slot)) continue;
        const targetSlot = slot;
        const wantedCh = answerSlots[targetSlot]!.ch;
        const delay = stepIdx * stepMs;
        stepIdx += 1;
        lastLandMs = delay + flipMs;

        setTimeout(() => {
          // Re-measure on the LIVE DOM at execution time. This is critical:
          // (a) prior steps may have shuffled `tiles`, so any sourceIdx
          //     captured at scheduling time would be stale.
          // (b) FLIP transforms from the previous step may still be in
          //     flight, so we must read positions fresh.
          if (snappedSet.has(targetSlot)) return; // defensive — shouldn't happen
          // Find a remaining unsnapped tile carrying the wanted character.
          // Scan from index 0 — tile order changed since scheduling.
          let sourceIdx = -1;
          for (let i = 0; i < tiles.length; i++) {
            if (!tiles[i]!.snapped && tiles[i]!.ch === wantedCh) {
              sourceIdx = i;
              break;
            }
          }
          if (sourceIdx < 0) {
            // No matching tile (shouldn't happen for valid answers); mark
            // the slot snapped and continue so the round still wraps.
            tiles[targetSlot]!.snapped = true;
            snappedSet.add(targetSlot);
            tiles[targetSlot]!.el.classList.add('is-snapped');
            return;
          }
          // Fresh "first" rects for every tile, taken AFTER any prior FLIP
          // transition has had its frame to commit.
          const firsts = tiles.map((t) => t.el.getBoundingClientRect());
          if (sourceIdx !== targetSlot) {
            const tmp = tiles[targetSlot]!;
            tiles[targetSlot] = tiles[sourceIdx]!;
            tiles[sourceIdx] = tmp;
          }
          tiles[targetSlot]!.snapped = true;
          snappedSet.add(targetSlot);
          render();
          flipTilesFrom(tiles, firsts, flipMs);
          requestAnimationFrame(() => {
            const t = tiles[targetSlot]!;
            t.el.classList.add('is-flashing');
            setTimeout(() => t.el.classList.remove('is-flashing'), flipMs);
          });
        }, delay);
      }

      setTimeout(resolve, Math.max(lastLandMs + 80, 600));
    });
  }

  return {
    el: wrap,
    setWord,
    snapHintLetter,
    cascadeReveal,
    dispose() {
      wrap.remove();
    },
  };
}
